import { useState, useEffect, useCallback } from "react";
import { SPECIALIST_HOSTNAME } from "env-vars";

/** Matches specialist ContentController / GenerateAiContent return shape */
export interface AiContentResponse {
  content: string | Record<string, unknown>;
  format: "html" | "json";
  metadata?: {
    model?: string;
    tokens_used?: number;
    prompt_tokens?: number;
    completion_tokens?: number;
  };
}

/** JSON content shape from specialist */
export interface AiContentJsonShape {
  heading?: string;
  body?: string;
  cta_text?: string;
  cta_url?: string;
  urgency?: string;
  key_points?: string[];
  prompt_received?: string;
  [key: string]: unknown;
}

/** AI prompt content (raw response) or static HTML string for non-AI items */
export type AiContentData = AiContentResponse | string;

// Module-level cache so generated content survives component unmount/remount (e.g. accordion toggle).
const contentCache = new Map<string, AiContentResponse>();

interface UseAiContentOptions {
  prompt: string;
  isAiPrompt: boolean;
  skip?: boolean;
}

interface UseAiContentResult {
  content: AiContentData | null;
  isLoading: boolean;
  error: string | null;
  retry: () => void;
}

/**
 * Hook to fetch AI-generated content from the specialist API.
 *
 * When isAiPrompt is true, returns the raw AiContentResponse.
 * When isAiPrompt is false, returns the prompt string as-is (static HTML content).
 */
export function useAiContent({
  prompt,
  isAiPrompt,
  skip = false,
}: UseAiContentOptions): UseAiContentResult {
  const cached = isAiPrompt ? contentCache.get(prompt) : undefined;
  const [content, setContent] = useState<AiContentData | null>(cached ?? null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const fetchAiContent = useCallback(async () => {
    if (!isAiPrompt) {
      setContent(prompt);
      setIsLoading(false);
      setError(null);
      return;
    }

    if (!prompt || skip) {
      setContent(null);
      setIsLoading(false);
      return;
    }

    const patientIdFromAuth0 = localStorage.getItem("ciq_id");
    const patientIdFromQuestionnaire = localStorage.getItem(
      "ciq-questionnaire.sessionId"
    );
    const patientId = patientIdFromAuth0 || patientIdFromQuestionnaire;
    const idToken = localStorage.getItem("id_token");

    if (!patientId) {
      setError("Patient ID not found");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${SPECIALIST_HOSTNAME}/api/v1/people/${patientId}/content/generate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(idToken && { Authorization: `Bearer ${idToken}` }),
          },
          body: JSON.stringify({
            prompt,
            output_format: "html",
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData?.error?.message ||
            `Failed to generate content: ${response.status}`
        );
      }

      const data: AiContentResponse = await response.json();
      contentCache.set(prompt, data);
      setContent(data);
      setError(null);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to generate AI content";
      setError(errorMessage);
      setContent(null);
      console.error("Failed to fetch AI content:", err);
    } finally {
      setIsLoading(false);
    }
  }, [prompt, isAiPrompt, skip]);

  useEffect(() => {
    if (!cached) fetchAiContent();
  }, [fetchAiContent, retryCount]);

  const retry = useCallback(() => {
    setRetryCount((prev) => prev + 1);
  }, []);

  return {
    content,
    isLoading,
    error,
    retry,
  };
}
